import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { motion } from "framer-motion";
import { LUXURY, IMAGE_CARD_FRAME } from "data/guestLuxuryTheme";
import SectionHeader from "./SectionHeader";

const spring = { type: "spring", stiffness: 280, damping: 32, mass: 0.85 };

const EventsCarousel = ({ events = [], onSelect, onViewAll }) => {
  const containerRef = useRef(null);
  const pauseUntil = useRef(0);
  const touchStartX = useRef(0);
  const indexRef = useRef(0);
  const [index, setIndex] = useState(0);
  const [stride, setStride] = useState(0);
  const posters = useMemo(() => events.filter((event) => event?.image), [events]);

  const goTo = (nextIndex) => {
    if (posters.length === 0) return;
    const clamped = ((nextIndex % posters.length) + posters.length) % posters.length;
    indexRef.current = clamped;
    setIndex(clamped);
  };

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const measure = () => {
      const slide = node.querySelector("[data-event-slide]");
      if (slide) setStride(slide.getBoundingClientRect().width);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [posters.length]);

  useEffect(() => {
    if (posters.length < 2) return undefined;
    const tick = window.setInterval(() => {
      if (Date.now() < pauseUntil.current) return;
      goTo(indexRef.current + 1);
    }, 4800);
    return () => window.clearInterval(tick);
  }, [posters.length]);

  const pauseAuto = () => {
    pauseUntil.current = Date.now() + 9000;
  };

  const handleTouchStart = (event) => {
    touchStartX.current = event.touches[0].clientX;
    pauseAuto();
  };

  const handleTouchEnd = (event) => {
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (delta < -48) goTo(index + 1);
    else if (delta > 48) goTo(index - 1);
  };

  if (posters.length === 0) return null;

  return (
    <Box sx={{ mt: 2.4 }}>
      <SectionHeader title="Events" onAction={onViewAll} />

      <Box
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        sx={{
          overflow: "hidden",
          mx: -2,
          px: 2,
          py: 1.25,
          touchAction: "pan-y",
        }}
      >
        <motion.div
          animate={{ x: stride ? -index * stride : 0 }}
          transition={spring}
          style={{
            display: "flex",
            alignItems: "stretch",
            willChange: "transform",
          }}
        >
          {posters.map((event, i) => {
            const distance = Math.abs(i - index);
            const isActive = distance === 0;

            return (
              <Box
                key={event.id}
                data-event-slide
                sx={{
                  flex: "0 0 88%",
                  maxWidth: 360,
                  px: 1.1,
                  py: 0.35,
                }}
              >
                <motion.div
                  animate={{
                    scale: isActive ? 1 : distance === 1 ? 0.94 : 0.9,
                    opacity: isActive ? 1 : distance === 1 ? 0.72 : 0.48,
                    y: isActive ? 0 : 6,
                  }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  style={{ height: "100%" }}
                >
                  <Box
                    component={motion.button}
                    type="button"
                    onClick={() => onSelect?.(event)}
                    whileTap={{ scale: 0.985 }}
                    sx={{
                      appearance: "none",
                      WebkitTapHighlightColor: "transparent",
                      border: IMAGE_CARD_FRAME.border,
                      bgcolor: "transparent",
                      outline: "none",
                      p: 0,
                      width: "100%",
                      borderRadius: "20px",
                      overflow: "hidden",
                      cursor: "pointer",
                      boxShadow: isActive
                        ? IMAGE_CARD_FRAME.boxShadow
                        : "0 8px 18px rgba(0,0,0,0.18)",
                      transition: "box-shadow 0.45s ease",
                      "&:focus": { outline: "none" },
                    }}
                  >
                    <Box sx={{ overflow: "hidden", height: 168 }}>
                      <Box
                        component={motion.img}
                        src={event.image}
                        alt={event.title}
                        animate={{ scale: isActive ? 1.06 : 1 }}
                        transition={{
                          duration: isActive ? 4.8 : 0.45,
                          ease: isActive ? "linear" : [0.22, 1, 0.36, 1],
                        }}
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "block",
                          objectFit: "cover",
                          objectPosition: "center",
                        }}
                      />
                    </Box>
                  </Box>
                </motion.div>
              </Box>
            );
          })}
        </motion.div>
      </Box>

      {posters.length > 1 ? (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.7, mt: 1.1 }}>
          {posters.map((event, i) => (
            <Box
              key={event.id}
              component={motion.button}
              type="button"
              aria-label={`Show ${event.title}`}
              onClick={() => {
                pauseAuto();
                goTo(i);
              }}
              animate={{
                width: i === index ? 16 : 7,
                opacity: i === index ? 1 : 0.45,
              }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              sx={{
                appearance: "none",
                border: 0,
                p: 0,
                height: 7,
                borderRadius: 999,
                bgcolor: LUXURY.gold,
                cursor: "pointer",
              }}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

export default EventsCarousel;
